import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronLeft, ChevronRight, BellDot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { listTabNudges, listTabNudgeTargets, updateTabNudge } from '../services/tabNudgeService';
import { TabNudge, TabNudgeTarget } from '../types/TabNudge';
import { DashTenant, tenantDisplayName } from '../types/Show';
import styles from '../styles/App.module.css';

const PAGE_SIZE = 20;

type TenantFilter = DashTenant | 'ALL';
type StatusFilter = 'ALL' | 'LIVE' | 'NOT_LIVE';

const formatWindow = (startAt: number, endAt: number | null): string => {
  const start = new Date(startAt).toLocaleString();
  const end = endAt === null ? 'Open-ended' : new Date(endAt).toLocaleString();
  return `${start} → ${end}`;
};

export default function TabNudgeManager() {
  const navigate = useNavigate();
  const [tenantFilter, setTenantFilter] = useState<TenantFilter>('ALL');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [page, setPage] = useState(0);
  const [nudges, setNudges] = useState<TabNudge[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [targetsById, setTargetsById] = useState<Record<string, TabNudgeTarget>>({});

  useEffect(() => {
    listTabNudgeTargets()
      .then(response => {
        const byId: Record<string, TabNudgeTarget> = {};
        response.targets.forEach(target => {
          byId[target.targetId] = target;
        });
        setTargetsById(byId);
      })
      .catch(() => toast.error('Failed to load nudgeable tabs'));
  }, []);

  const loadNudges = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listTabNudges({
        tenant: tenantFilter === 'ALL' ? undefined : tenantFilter,
        page,
        size: PAGE_SIZE,
      });
      setNudges(response.items);
      setTotal(response.total);
    } catch {
      toast.error('Failed to load tab nudges');
    } finally {
      setLoading(false);
    }
  }, [tenantFilter, page]);

  useEffect(() => {
    loadNudges();
  }, [loadNudges]);

  const handleTenantChange = (value: TenantFilter) => {
    setTenantFilter(value);
    setPage(0);
  };

  const handleStatusChange = (value: StatusFilter) => {
    setStatusFilter(value);
  };

  const filteredNudges =
    statusFilter === 'ALL'
      ? nudges
      : nudges.filter(n => (statusFilter === 'LIVE' ? n.liveNow : !n.liveNow));

  const handleToggleEnabled = async (nudge: TabNudge, enabled: boolean) => {
    setTogglingId(nudge.id);
    try {
      const updated = await updateTabNudge(nudge.id, { enabled });
      setNudges(prev => prev.map(n => (n.id === nudge.id ? updated : n)));
      toast.success(`Tab nudge ${enabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update tab nudge');
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreate = () => navigate('/tab-nudges/create');
  const handleEdit = (id: string) => navigate(`/tab-nudges/${id}/edit`);

  const hasPrevious = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < total;

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.contentWrapper}>
        {/* Navigation */}
        <div className={styles.navigation}>
          <div className={styles.navContent}>
            <h1 className={styles.navTitle}>Tab Nudge Manager</h1>
            <Button variant="secondary" size="sm" onClick={handleCreate} className={styles.navButton}>
              <Plus className="h-4 w-4 mr-2" />
              Create New
            </Button>
          </div>
        </div>

        <div className="px-4 mt-8">
          {/* Filters */}
          <div className="mb-8 flex items-center justify-end gap-3">
            <Select value={statusFilter} onValueChange={handleStatusChange}>
              <SelectTrigger className="sm:w-40">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                <SelectItem value="LIVE">Live now</SelectItem>
                <SelectItem value="NOT_LIVE">Not live</SelectItem>
              </SelectContent>
            </Select>
            <Select value={tenantFilter} onValueChange={handleTenantChange}>
              <SelectTrigger className="sm:w-48">
                <SelectValue placeholder="Filter by tenant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All tenants</SelectItem>
                <SelectItem value="REELS">{tenantDisplayName.REELS}</SelectItem>
                <SelectItem value="SHORTFREE">{tenantDisplayName.SHORTFREE}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Loading */}
          {loading && (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={`skeleton-${index}`} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          )}

          {/* Empty */}
          {!loading && filteredNudges.length === 0 && (
            <div className="flex flex-col items-center justify-center text-center py-16">
              <BellDot className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">
                {statusFilter !== 'ALL' ? 'No matching nudges' : 'No tab nudges yet'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {statusFilter !== 'ALL'
                  ? 'Try a different status or tenant filter.'
                  : 'Create one to show a red dot on a home tab in the app.'}
              </p>
              {statusFilter === 'ALL' && (
                <Button onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Tab Nudge
                </Button>
              )}
            </div>
          )}

          {/* Table */}
          {!loading && filteredNudges.length > 0 && (
            <div className="rounded-lg border bg-card overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tab</TableHead>
                    <TableHead>Screens</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Window</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Enabled</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredNudges.map(nudge => {
                    const target = targetsById[nudge.targetId];
                    const screens = target ? Array.from(new Set(target.screens)) : [];
                    return (
                      <TableRow
                        key={nudge.id}
                        onClick={() => handleEdit(nudge.id)}
                        className="cursor-pointer"
                      >
                        <TableCell className="min-w-[200px]">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">
                              {target ? target.displayName : nudge.targetId}
                            </span>
                            {/* Tab removed from all layouts since the nudge was created */}
                            {!target && <Badge variant="destructive">Orphaned</Badge>}
                          </div>
                          <div className="text-xs text-muted-foreground">{nudge.targetId}</div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {screens.length === 0 ? (
                              <span className="text-sm text-muted-foreground">—</span>
                            ) : (
                              screens.map(screen => (
                                <Badge key={screen} variant="outline">
                                  {screen}
                                </Badge>
                              ))
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{tenantDisplayName[nudge.tenant]}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatWindow(nudge.startAt, nudge.endAt)}
                        </TableCell>
                        <TableCell>
                          {nudge.liveNow ? (
                            <Badge>Live now</Badge>
                          ) : (
                            <Badge variant="secondary">Not live</Badge>
                          )}
                        </TableCell>
                        {/* Inline toggle — stop row navigation when interacting */}
                        <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                          <Switch
                            checked={nudge.enabled}
                            disabled={togglingId === nudge.id}
                            onCheckedChange={checked => handleToggleEnabled(nudge, checked)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {!loading && filteredNudges.length > 0 && (hasPrevious || hasNext) && (
            <div className="flex justify-between items-center mt-8 p-4 bg-card rounded-lg border">
              <div className="text-sm text-muted-foreground">
                Page {page + 1} — {total} total
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={!hasPrevious}
                  className="h-8 px-3"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasNext}
                  className="h-8 px-3"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
